import React, { useState } from 'react'
import {
  List,
  Datagrid,
  TextField,
  DateField,
  useTranslate,
  FunctionField,
  TextInput,
  SelectInput,
  DateInput,
  useDeleteMany,
  useNotify,
  useRefresh,
  useUnselectAll,
  useListContext,
  useRecordContext,
  useDelete,
  Confirm,
  ShowButton,
} from 'react-admin'
import { Container, Box, Button } from '@mui/material'
import { CardCommon } from 'src/components/uiElements'
import { ListViewTopToolbar } from 'src/components/uiElements/CRUD/ListViewTopToolbar'
import IconButton from '@mui/material/IconButton'
import LinkIcon from '@mui/icons-material/Link'
import DeleteIcon from '@mui/icons-material/Delete'
import Tooltip from '@mui/material/Tooltip'

// Toolbar personalizado que se muestra encima de la tabla
const CustomBulkActionsToolbar = () => {
  const translate = useTranslate()
  const { selectedIds } = useListContext()
  const [deleteMany, { isLoading }] = useDeleteMany()
  const notify = useNotify()
  const refresh = useRefresh()
  const unselectAll = useUnselectAll('operation-bff')
  const [open, setOpen] = useState(false)

  if (selectedIds.length === 0) return null

  const handleClick = () => setOpen(true)
  const handleDialogClose = () => setOpen(false)

  const handleConfirm = () => {
    deleteMany(
      'operation-bff',
      { ids: selectedIds },
      {
        onSuccess: () => {
          notify('ra.notification.deleted', { type: 'success' })
          unselectAll()
          refresh()
        },
        onError: () => {
          notify('ra.notification.http_error', { type: 'error' })
        },
      }
    )
    setOpen(false)
  }

  return (
    <Box
      sx={{
        backgroundColor: '#e3f2fd',
        borderRadius: '4px',
        padding: '10px 16px',
        marginBottom: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <span>{selectedIds.length} seleccionado(s)</span>
      <Button
        color='error'
        variant='contained'
        onClick={handleClick}
        disabled={isLoading}
        startIcon={<DeleteIcon />}
        size='small'
      >
        {translate('ra.action.delete')}
      </Button>
      <Confirm
        isOpen={open}
        title={`Eliminar ${selectedIds.length} operaciones`}
        content={`¿Está seguro de que desea eliminar ${selectedIds.length} operaciones? Esta acción no se puede deshacer.`}
        onConfirm={handleConfirm}
        onClose={handleDialogClose}
      />
    </Box>
  )
}

// Botón de delete individual con stopPropagation
const DeleteOperationButton = () => {
  const record = useRecordContext()
  const translate = useTranslate()
  const [deleteOne, { isLoading }] = useDelete()
  const notify = useNotify()
  const refresh = useRefresh()
  const [open, setOpen] = useState(false)

  const handleClick = (e) => {
    e.stopPropagation()
    setOpen(true)
  }

  const handleDialogClose = (e) => {
    e?.stopPropagation()
    setOpen(false)
  }

  const handleConfirm = (e) => {
    e?.stopPropagation()
    deleteOne(
      'operation-bff',
      { id: record.id, previousData: record },
      {
        onSuccess: () => {
          notify('ra.notification.deleted', { type: 'success' })
          refresh()
        },
        onError: () => {
          notify('ra.notification.http_error', { type: 'error' })
        },
      }
    )
    setOpen(false)
  }

  const operationName = record?.operation_id || record?.hash_operation?.substring(0, 12) || ''

  return (
    <>
      <Tooltip title={translate('ra.action.delete')} placement='top'>
        <IconButton
          size='small'
          onClick={handleClick}
          disabled={isLoading}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon fontSize='small' />
        </IconButton>
      </Tooltip>
      <Confirm
        isOpen={open}
        title={`Eliminar operación ${operationName}`}
        content='¿Está seguro de que desea eliminar esta operación? Esta acción no se puede deshacer.'
        onConfirm={handleConfirm}
        onClose={handleDialogClose}
      />
    </>
  )
}

// Estilos base para los chips de estado
const baseStatusStyle = {
  borderRadius: '5px',
  color: 'white',
  padding: '2px 7px',
  fontSize: 14,
  textAlign: 'center',
  display: 'inline-block',
}

// Colores para estado general (soporta con espacios y sin espacios)
const getStatusGeneralStyle = (status) => {
  const colors = {
    // Rojo
    'ERROR': '#ff0000',
    'ELIMINADO': '#ff0000',
    // Amarillo
    'EN SITIO': '#ffff00',
    'EN_SITIO': '#ffff00',
    'CANCELADO': '#ffff00',
    // Verde
    'RESERVADO': '#22ee65',
  }

  const backgroundColor = colors[status] || '#gray'
  const textColor = ['#ffff00'].includes(backgroundColor) ? '#000' : '#fff'

  return {
    ...baseStatusStyle,
    backgroundColor,
    color: textColor,
  }
}

// Colores para estado flow (soporta con espacios y sin espacios)
const getStatusFlowStyle = (status) => {
  const colors = {
    // Rojo
    'BOOKING FALLIDO': '#ff0000',
    'BOOKING_FALLIDO': '#ff0000',
    // Verde
    'SOLICITUD CREADA': '#22ee65',
    'SOLICITUD_CREADA': '#22ee65',
    'PTP TREN CREADO': '#22ee65',
    'PTP_TREN_CREADO': '#22ee65',
    'PASE TREN CREADO': '#22ee65',
    'PASE_TREN_CREADO': '#22ee65',
    'PAGO EN PROCESO': '#22ee65',
    'PAGO_EN_PROCESO': '#22ee65',
    'AEREO CREADO': '#22ee65',
    'AEREO_CREADO': '#22ee65',
    // Amarillo
    'FRAUDE': '#ffff00',
    'SESION EXPIRADA': '#ffff00',
    'SESION_EXPIRADA': '#ffff00',
    'SESION CERRADA': '#ffff00',
    'SESION_CERRADA': '#ffff00',
    'PAGO FALLIDO': '#ffff00',
    'PAGO_FALLIDO': '#ffff00',
    // Naranja
    'CREADO': '#ffa500',
  }

  const backgroundColor = colors[status] || '#gray'
  const textColor = ['#ffff00'].includes(backgroundColor) ? '#000' : '#fff'

  return {
    ...baseStatusStyle,
    backgroundColor,
    color: textColor,
  }
}

// Componente para renderizar estado general
const StatusGeneralField = ({ record }) => {
  const status = record?.status_general
  if (!status) return '-'

  return (
    <Box component="span" sx={getStatusGeneralStyle(status)}>
      {status}
    </Box>
  )
}

// Componente para renderizar estado flow
const StatusFlowField = ({ record }) => {
  const status = record?.status_flow
  if (!status) return '-'

  return (
    <Box component="span" sx={getStatusFlowStyle(status)}>
      {status}
    </Box>
  )
}

const CustomReservationButton = () => {
  const record = useRecordContext()
  const translate = useTranslate()

  if (!record?.reservation_id) return null

  const handleClick = (e) => {
    e.stopPropagation()
    window.open(`/#/reservation/${record.reservation_id}/show`, '_blank')
  }

  return (
    <Tooltip
      title={translate('ra.action.see_something', { thing: translate('ra.entity.reservation') })}
      placement='top'
    >
      <IconButton size='small' onClick={handleClick} aria-label='view reservation'>
        <LinkIcon fontSize='small' />
      </IconButton>
    </Tooltip>
  )
}

// Columna de acciones personalizada
const ActionsColumn = () => {
  const translate = useTranslate()

  return (
    <Box sx={{ display: 'flex', gap: 0.5 }}>
      <Tooltip title={translate('ra.action.show')} placement='top'>
        <span>
          <ShowButton label='' />
        </span>
      </Tooltip>
      <CustomReservationButton />
      <DeleteOperationButton />
    </Box>
  )
}

// Wrapper que incluye el toolbar personalizado y el datagrid
const ListContent = ({ translate, getOperationId, getProductName, getWorkUnit, getReservationNumber }) => {
  return (
    <>
      <CustomBulkActionsToolbar />
      <Datagrid rowClick='show' bulkActionButtons={<></>}>
        <DateField
          source='created_at'
          label={translate('ra.field.created_at')}
          showTime
        />
        <FunctionField
          label={translate('ra.field.operation_id')}
          render={getOperationId}
        />
        <FunctionField
          label={translate('ra.field.product_type')}
          render={getProductName}
        />
        <FunctionField
          label={translate('ra.field.work_unit')}
          render={getWorkUnit}
        />
        <FunctionField
          label={translate('ra.field.general_state')}
          render={(record) => <StatusGeneralField record={record} />}
        />
        <FunctionField
          label={translate('ra.field.flow_status')}
          render={(record) => <StatusFlowField record={record} />}
        />
        <FunctionField
          label={translate('ra.field.reservation_number')}
          render={getReservationNumber}
        />
        <FunctionField
          label={translate('ra.action.actions')}
          render={() => <ActionsColumn />}
        />
      </Datagrid>
    </>
  )
}

const OperationBffFilters = [
  <DateInput
    key='created_at'
    source='created_at'
    label='ra.field.created_at'
    alwaysOn
  />,
  <TextInput
    key='operation_id'
    source='operation_id'
    label='ra.field.operation_id'
  />,
  <TextInput
    key='work_unit'
    source='work_unit'
    label='ra.field.work_unit'
  />,
  <SelectInput
    key='product_type'
    source='product_type'
    label='ra.field.product_type'
    choices={[
      { id: 'TRAINS', name: 'Trenes' },
      { id: 'AIR', name: 'Aéreo' },
      { id: 'CARS', name: 'Autos' },
    ]}
  />,
  <SelectInput
    key='currency'
    source='currency'
    label='ra.field.currency'
    choices={[
      { id: 'ARS', name: 'ARS' },
      { id: 'USD', name: 'USD' },
      { id: 'EUR', name: 'EUR' },
      { id: 'BRL', name: 'BRL' },
    ]}
  />,
  <TextInput
    key='booking_references'
    source='booking_references'
    label='ra.field.locator'
  />,
  <SelectInput
    key='device'
    source='device'
    label='ra.field.device'
    choices={[
      { id: 'desktop', name: 'Desktop' },
      { id: 'mobile', name: 'Mobile' },
    ]}
  />,
  <SelectInput
    key='payment_type'
    source='payment_type'
    label='ra.field.payment_type'
    choices={[
      { id: 'TC', name: 'Tarjeta Crédito' },
      { id: 'TD', name: 'Tarjeta Débito' },
      { id: 'PIX', name: 'PIX' },
    ]}
  />,
  <SelectInput
    key='supplier'
    source='supplier'
    label='ra.field.supplier'
    choices={[
      { id: 'DISTRIBUSION', name: 'Distribusion' },
      { id: 'RAIL_EUROPE', name: 'Rail Europe' },
    ]}
  />,
]

const OperationBffList = (props) => {
  const translate = useTranslate()

  const getOperationId = (record) => {
    // Usar operation_id cuando esté disponible, sino mostrar hash truncado
    if (record?.operation_id) {
      return record.operation_id
    }
    return record?.hash_operation?.substring(0, 12) + '...' || '-'
  }

  const getProductName = (record) => {
    return record?.product_name || '-'
  }

  const getWorkUnit = (record) => {
    // Usar work_unit_short_name cuando esté disponible
    if (record?.work_unit_short_name) {
      return record.work_unit_short_name
    }
    // Fallback a country_code
    return record?.country_code || '-'
  }

  const getReservationNumber = (record) => {
    return record?.reservation_id_b2 || '-'
  }

  return (
    <Container className='content'>
      <CardCommon title={translate('ra.entity.operation-bff')}>
        <List
          {...props}
          exporter={false}
          component='div'
          sort={{ field: 'created_at', order: 'DESC' }}
          filters={OperationBffFilters}
          actions={<ListViewTopToolbar hasCreate={false} />}
          sx={{
            '& .RaBulkActionsToolbar-root, & .RaBulkActionsToolbar-toolbar, & .RaBulkActionsToolbar-title, & .RaBulkActionsToolbar-topToolbar': {
              display: 'none !important',
              visibility: 'hidden !important',
              height: '0 !important',
              minHeight: '0 !important',
              padding: '0 !important',
              margin: '0 !important',
              overflow: 'hidden !important',
            },
          }}
        >
          <ListContent
            translate={translate}
            getOperationId={getOperationId}
            getProductName={getProductName}
            getWorkUnit={getWorkUnit}
            getReservationNumber={getReservationNumber}
          />
        </List>
      </CardCommon>
    </Container>
  )
}

export default OperationBffList
